import { useEffect } from 'react'
import { useFrame } from '@react-three/fiber'
import { FrontSide, RepeatWrapping, SRGBColorSpace, ShaderMaterial, Texture, Vector2 } from 'three'
import {
  tunnelShaderRotationCenterY,
  tunnelShaderTextureRotationSpeed,
  tunnelShaderTextureScale,
} from '@/src/game/config/constants'
import { textureAssetCompressed } from '@/src/game/assetStore'
import { useCompressedTexture } from '../../ktx2Textures'

export function useUpdateTunnelShaderStele() {
  const tunnelTexture = useCompressedTexture(textureAssetCompressed.tunnel)
  configureTexture(tunnelTexture)

  useEffect(() => {
    tunnelShaderStele.uniforms.uTunnelTexture.value = tunnelTexture
  }, [tunnelTexture])

  useFrame((state) => {
    state.gl.getDrawingBufferSize(tunnelShaderStele.uniforms.uResolution.value)
    const resolution = tunnelShaderStele.uniforms.uResolution.value as Vector2
    tunnelShaderStele.uniforms.uInvResolution.value.set(
      1 / Math.max(resolution.x, 1),
      1 / Math.max(resolution.y, 1),
    )
    tunnelShaderStele.uniforms.uAspect.value =
      resolution.x / Math.max(resolution.y, 0.0001)

    const angle = state.clock.elapsedTime * tunnelShaderTextureRotationSpeed
    tunnelShaderStele.uniforms.uRotationCS.value.set(Math.cos(angle), Math.sin(angle))
  })
}

function configureTexture(texture: Texture) {
  texture.colorSpace = SRGBColorSpace
  texture.wrapS = RepeatWrapping
  texture.wrapT = RepeatWrapping
}

export const tunnelShaderStele: ShaderMaterial = new ShaderMaterial({
  side: FrontSide,
  transparent: false,
  toneMapped: false,
  uniforms: {
      uResolution: { value: new Vector2(1, 1) },
      uInvResolution: { value: new Vector2(1, 1) },
      uAspect: { value: 1 },
      uRotationCS: { value: new Vector2(1, 0) },
      uRotationCenterY: { value: tunnelShaderRotationCenterY },
      uInvTextureScale: { value: 1 / Math.max(tunnelShaderTextureScale, 0.0001) },
      uTunnelTexture: { value: null },
  },
  vertexShader: `
    void main() {
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }`,
  fragmentShader: `
    uniform vec2 uInvResolution;
    uniform float uAspect;
    uniform vec2 uRotationCS;
    uniform float uRotationCenterY;
    uniform float uInvTextureScale;
    uniform sampler2D uTunnelTexture;

    void main() {
      vec2 screenUv = gl_FragCoord.xy * uInvResolution;
      vec2 screenRotationCenter = vec2(0.5, uRotationCenterY);
      vec2 textureRotationCenter = vec2(0.5, 0.5);
      vec2 centeredScreenUv = screenUv - screenRotationCenter;
      centeredScreenUv.x *= uAspect;
      vec2 rotatedTextureUv = vec2(
        centeredScreenUv.x * uRotationCS.x - centeredScreenUv.y * uRotationCS.y,
        centeredScreenUv.x * uRotationCS.y + centeredScreenUv.y * uRotationCS.x
      );
      rotatedTextureUv.x /= uAspect;
      vec2 sampleUv = rotatedTextureUv * uInvTextureScale + textureRotationCenter;
      vec3 color = texture2D(uTunnelTexture, sampleUv).rgb;
      gl_FragColor = vec4(color, 1.0);
    }`
})
